/**
 * GSC MULTI-SESSION CACHE — V2.2 (Vite/HMR-proof)
 * Authority: Senior Reliability Engineer
 *
 * Core idea:
 * - NEVER rely on module-scope singletons in Vite dev (HMR / duplicate module graphs can occur).
 * - Store everything in globalThis under stable keys to guarantee cross-module consistency.
 */

import type { AnalysisResult } from "../types";

interface CachedPayload {
  payload: any;
  harmonyOverride: number | null;
  timestamp: number;
}

type GlobalCacheShape = {
  sessionCache: Map<string, CachedPayload>;
  lastSessionId: string | null;
  lastFullResult: AnalysisResult | null;
};

// ---- Global keys (must be stable) ----
const GLOBAL_KEY = "__GSC_ANALYSIS_CACHE_V2__";
const GLOBAL_LAST_RESULT_KEY = "__GSC_LAST_RESULT__"; // legacy-friendly key you already log

function getGlobalCache(): GlobalCacheShape {
  const g = globalThis as any;

  if (!g[GLOBAL_KEY]) {
    g[GLOBAL_KEY] = {
      sessionCache: new Map<string, CachedPayload>(),
      lastSessionId: null,
      lastFullResult: null,
    } satisfies GlobalCacheShape;
  }

  return g[GLOBAL_KEY] as GlobalCacheShape;
}

// Safe deep clone for payloads only (avoid structuredClone incompat on older env)
function deepCloneJSON<T>(x: T): T {
  if (x == null) return x;
  try {
    return JSON.parse(JSON.stringify(x));
  } catch {
    // If payload contains non-serializable objects, fall back to raw reference.
    // (Should not happen for DSP payload, but we keep the engine fail-soft.)
    return x;
  }
}

/**
 * Store RAW DSP payload (sanitized) + optional harmonyOverride for a given sessionId
 */
export function setLastAnalysisRawPayload(
  sessionId: string,
  payload: any,
  harmonyOverride?: number | null
) {
  const cache = getGlobalCache();
  cache.lastSessionId = sessionId;

  cache.sessionCache.set(sessionId, {
    payload: payload ? deepCloneJSON(payload) : null,
    harmonyOverride: harmonyOverride !== undefined ? harmonyOverride : null,
    timestamp: Date.now(),
  });
}

/**
 * Store final AnalysisResult (FULL) — also mirrored to a legacy global key
 * so any debug tooling can read it even if another module copy is used.
 */
export function setLastResult(res: AnalysisResult) {
  const cache = getGlobalCache();
  cache.lastFullResult = res;

  // Hard guarantee across Vite/HMR/module duplication
  (globalThis as any)[GLOBAL_LAST_RESULT_KEY] = res;

  // Optional dev breadcrumb (no spam in prod)
  try {
    if ((import.meta as any)?.env?.DEV) {
      // eslint-disable-next-line no-console
      console.log("[ANALYSIS_CACHE] setLastResult OK:", {
        hasSession: !!res?.audit_trail?.scoring_ruleset_id,
        score: res?.session?.score ?? null,
      });
    }
  } catch {}
}

/**
 * Read last full result:
 * - prefer global cache object
 * - fallback to legacy global key
 */
export function getLastResult(): AnalysisResult | null {
  const cache = getGlobalCache();
  return cache.lastFullResult || ((globalThis as any)[GLOBAL_LAST_RESULT_KEY] ?? null);
}

/**
 * Read last raw payload for a session id (or last known session)
 */
export function getLastAnalysisRawPayload(sessionId?: string): any | null {
  const cache = getGlobalCache();
  const id = sessionId || cache.lastSessionId;
  if (!id) return null;
  return cache.sessionCache.get(id)?.payload || null;
}

/**
 * Read harmony override used for the session (or last known session)
 */
export function getLastHarmonyOverride(sessionId?: string): number | null {
  const cache = getGlobalCache();
  const id = sessionId || cache.lastSessionId;
  if (!id) return null;
  return cache.sessionCache.get(id)?.harmonyOverride || null;
}

/**
 * Clear raw payload for a session (or last known session)
 */
export function clearLastAnalysisRawPayload(sessionId?: string) {
  const cache = getGlobalCache();
  const id = sessionId || cache.lastSessionId;

  if (id) {
    cache.sessionCache.delete(id);
    if (id === cache.lastSessionId) cache.lastSessionId = null;
  }
}

/**
 * Clear a session cache entry
 */
export function clearSessionCache(sessionId: string) {
  const cache = getGlobalCache();
  if (sessionId === cache.lastSessionId) cache.lastSessionId = null;
  cache.sessionCache.delete(sessionId);
}

/**
 * Optional: clear FULL result (rarely needed, but useful for debug buttons)
 */
export function clearLastResult() {
  const cache = getGlobalCache();
  cache.lastFullResult = null;
  try {
    delete (globalThis as any)[GLOBAL_LAST_RESULT_KEY];
  } catch {
    (globalThis as any)[GLOBAL_LAST_RESULT_KEY] = null;
  }
}
